import { useTranslation } from '@jq4race/i18n';
import type { UserListItem } from '@jq4race/shared';
import {
  Badge,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from '@jq4race/ui';
import { RefreshCw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { formatDateTime } from '../../../../shared/format/format-date';
import { Pagination } from './Pagination';
import { Select } from './Select';

type ManageableRole = 'supervisor' | 'operator';
type RoleFilter = 'all' | UserListItem['role'];
type StatusFilter = 'all' | 'active' | 'inactive';
type SortKey = 'email' | 'role' | 'status' | 'lastLogin';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 10;

const ROLE_LABEL_KEYS: Record<UserListItem['role'], string> = {
  super_admin: 'roles.superAdmin',
  org_admin: 'roles.orgAdmin',
  supervisor: 'roles.supervisor',
  operator: 'roles.operator',
};

const ROLE_ORDER: Record<UserListItem['role'], number> = {
  super_admin: 0,
  org_admin: 1,
  supervisor: 2,
  operator: 3,
};

interface MembersTableProps {
  users: UserListItem[];
  loading: boolean;
  actorId: string | null;
  isOrgAdmin: boolean;
  /** Only super_admin viewers get the "Super administrator" role filter option. */
  showSuperAdminFilter?: boolean;
  actingOn: string | null;
  onRefresh: () => void;
  onRoleChange: (user: UserListItem, role: ManageableRole) => void;
  onDeactivate: (user: UserListItem) => void;
  onReactivate: (user: UserListItem) => void;
  onShowSessions: (user: UserListItem) => void;
}

export function MembersTable({
  users,
  loading,
  actorId,
  isOrgAdmin,
  showSuperAdminFilter = false,
  actingOn,
  onRefresh,
  onRoleChange,
  onDeactivate,
  onReactivate,
  onShowSessions,
}: MembersTableProps) {
  const { t, i18n } = useTranslation('users');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('email');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users.filter((u) => {
      if (term && !(u.email ?? '').toLowerCase().includes(term)) return false;
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (statusFilter === 'active' && !u.active) return false;
      if (statusFilter === 'inactive' && u.active) return false;
      return true;
    });
  }, [users, search, roleFilter, statusFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      switch (sortKey) {
        case 'email':
          return (a.email ?? '').localeCompare(b.email ?? '') * dir;
        case 'role':
          return (ROLE_ORDER[a.role] - ROLE_ORDER[b.role]) * dir;
        case 'status':
          return ((a.active ? 0 : 1) - (b.active ? 0 : 1)) * dir;
        case 'lastLogin': {
          const av = a.lastLoginAt ? new Date(a.lastLoginAt).getTime() : 0;
          const bv = b.lastLoginAt ? new Date(b.lastLoginAt).getTime() : 0;
          return (av - bv) * dir;
        }
        default:
          return 0;
      }
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const sortFor = (key: SortKey): SortDir | null => (sortKey === key ? sortDir : null);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-sm flex-1">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-jq-stone"
            strokeWidth={1.5}
          />
          <Input
            type="search"
            placeholder={t('membersTable.searchPlaceholder')}
            className="h-9 pl-9 text-[13px]"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select
            selectSize="sm"
            aria-label={t('membersTable.filterByRole')}
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value as RoleFilter);
              setPage(1);
            }}
            className="min-w-[140px]"
          >
            <option value="all">{t('membersTable.allRoles')}</option>
            <option value="org_admin">{t('roles.orgAdmin')}</option>
            <option value="supervisor">{t('roles.supervisor')}</option>
            <option value="operator">{t('roles.operator')}</option>
            {showSuperAdminFilter ? (
              <option value="super_admin">{t('roles.superAdmin')}</option>
            ) : null}
          </Select>

          <Select
            selectSize="sm"
            aria-label={t('membersTable.filterByStatus')}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as StatusFilter);
              setPage(1);
            }}
            className="min-w-[120px]"
          >
            <option value="all">{t('membersTable.all')}</option>
            <option value="active">{t('membersTable.active')}</option>
            <option value="inactive">{t('membersTable.deactivated')}</option>
          </Select>

          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
            aria-label={t('membersTable.refresh')}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.5} />
          </Button>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead
              sortable
              sortDirection={sortFor('email')}
              onClick={() => toggleSort('email')}
            >
              {t('membersTable.columns.email')}
            </TableHead>
            <TableHead sortable sortDirection={sortFor('role')} onClick={() => toggleSort('role')}>
              {t('membersTable.columns.role')}
            </TableHead>
            <TableHead
              sortable
              sortDirection={sortFor('status')}
              onClick={() => toggleSort('status')}
            >
              {t('membersTable.columns.status')}
            </TableHead>
            <TableHead
              sortable
              sortDirection={sortFor('lastLogin')}
              onClick={() => toggleSort('lastLogin')}
            >
              {t('membersTable.columns.lastLogin')}
            </TableHead>
            <TableHead align="right">&nbsp;</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && users.length === 0 ? (
            <TableSkeleton
              rows={5}
              columns={5}
              cellWidths={[
                'w-[200px]',
                'w-[120px]',
                'w-[72px] rounded-full',
                'w-[140px]',
                'ml-auto w-[80px]',
              ]}
            />
          ) : paged.length === 0 ? (
            <TableEmpty
              colSpan={5}
              message={
                users.length === 0 ? t('membersTable.empty') : t('membersTable.emptyFiltered')
              }
            />
          ) : (
            paged.map((u) => {
              const isSelf = u.id === actorId;
              const manageable = u.role === 'supervisor' || u.role === 'operator';
              const canEdit = isOrgAdmin && !isSelf && manageable && u.active;
              const canReactivate = isOrgAdmin && !isSelf && manageable && !u.active;
              const busy = actingOn === u.id;
              return (
                <TableRow key={u.id}>
                  <TableCell>
                    <span className="font-medium text-jq-charcoal">{u.email ?? '—'}</span>
                    {isSelf && (
                      <span className="ml-2 align-middle text-[11px] uppercase tracking-widest text-jq-stone">
                        {t('membersTable.you')}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {canEdit ? (
                      <Select
                        selectSize="sm"
                        value={u.role as ManageableRole}
                        onChange={(e) => onRoleChange(u, e.target.value as ManageableRole)}
                        disabled={busy}
                        className="min-w-[150px]"
                        aria-label={t('membersTable.changeRoleFor', { email: u.email })}
                      >
                        <option value="supervisor">{t('roles.supervisor')}</option>
                        <option value="operator">{t('roles.operator')}</option>
                      </Select>
                    ) : (
                      t(ROLE_LABEL_KEYS[u.role])
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={u.active ? 'success' : 'danger'}>
                      {u.active ? t('membersTable.active') : t('membersTable.deactivated')}
                    </Badge>
                  </TableCell>
                  <TableCell muted>{formatDateTime(u.lastLoginAt, i18n.language)}</TableCell>
                  <TableCell align="right">
                    <div className="flex justify-end gap-1">
                      {isOrgAdmin && (manageable || isSelf) && u.active && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onShowSessions(u)}
                          disabled={busy}
                        >
                          {t('membersTable.sessions')}
                        </Button>
                      )}
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeactivate(u)}
                          disabled={busy}
                          className="text-jq-danger hover:bg-jq-danger/10 hover:text-jq-danger"
                        >
                          {t('membersTable.deactivate')}
                        </Button>
                      )}
                      {canReactivate && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onReactivate(u)}
                          disabled={busy}
                        >
                          {t('membersTable.reactivate')}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <Pagination
        total={sorted.length}
        page={safePage}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />
    </div>
  );
}
